import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { meetApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Video, Plus, Users, PhoneOff, ExternalLink, Copy, Clock } from 'lucide-react';

export default function MeetPage() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRoom, setActiveRoom] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [roomName, setRoomName] = useState('');
  const iframeRef = useRef(null);

  useEffect(() => {
    if (roomId) {
      meetApi.getRoom(roomId).then(r => { setActiveRoom(r); setLoading(false); }).catch(() => { toast.error('Room not found'); navigate('/meet'); });
    } else {
      meetApi.getRooms().then(d => { setRooms(d.rooms || []); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [roomId]);

  const handleCreate = async () => {
    if (!roomName.trim()) { toast.error('Room name required'); return; }
    try {
      const room = await meetApi.createRoom({ name: roomName });
      setRooms(prev => [room, ...prev]);
      setShowCreate(false);
      setRoomName('');
      toast.success('Meeting room created');
      navigate(`/meet/${room.room_id}`);
    } catch (e) { toast.error(e.message); }
  };

  const handleEnd = async (roomId) => {
    try {
      await meetApi.endRoom(roomId);
      setActiveRoom(null);
      navigate('/meet');
      toast.success('Meeting ended');
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    try {
      await meetApi.deleteRoom(id);
      setRooms(prev => prev.filter(r => r.room_id !== id));
      toast.success('Room deleted');
    } catch (e) { toast.error(e.message); }
  };

  const copyLink = (room) => {
    navigator.clipboard.writeText(`${window.location.origin}/meet/${room.room_id}`);
    toast.success('Link copied!');
  };

  // Active meeting view with Jitsi embed
  if (activeRoom) {
    const jitsiUrl = `https://meet.jit.si/${activeRoom.jitsi_room}#userInfo.displayName="${encodeURIComponent(user?.name || 'User')}"`;
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="meet-active">
        <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
          <div className="max-w-full mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setActiveRoom(null); navigate('/meet'); }} data-testid="leave-btn"><ArrowLeft className="w-5 h-5" /></Button>
              <Video className="w-5 h-5 text-green-500" />
              <h1 className="text-lg font-bold text-foreground">{activeRoom.name}</h1>
              <span className="text-xs text-muted-foreground">Room: {activeRoom.jitsi_room}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => copyLink(activeRoom)}><Copy className="w-3.5 h-3.5 mr-1" /> Copy Link</Button>
              <Button variant="outline" size="sm" onClick={() => window.open(jitsiUrl, '_blank')}><ExternalLink className="w-3.5 h-3.5 mr-1" /> Pop Out</Button>
              {activeRoom.host_id === user?.user_id && (
                <Button variant="destructive" size="sm" onClick={() => handleEnd(activeRoom.room_id)} data-testid="end-meeting-btn"><PhoneOff className="w-3.5 h-3.5 mr-1" /> End Meeting</Button>
              )}
            </div>
          </div>
        </header>
        <div className="flex-1">
          <iframe ref={iframeRef} src={jitsiUrl} allow="camera; microphone; display-capture; autoplay; clipboard-write"
            className="w-full h-[calc(100vh-56px)] border-none" title="Jitsi Meet" data-testid="jitsi-iframe" />
        </div>
      </div>
    );
  }

  // Room listing
  return (
    <div className="min-h-screen bg-background" data-testid="meet-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <Video className="w-6 h-6 text-green-500" />
            <h1 className="text-xl font-bold text-foreground">Meet</h1>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-meeting-btn"><Plus className="w-4 h-4 mr-1" /> New Meeting</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-36 bg-muted animate-pulse rounded-xl" />)}</div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-20">
            <Video className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No meetings yet</h2>
            <p className="text-muted-foreground mb-6">Create a meeting room to start a video call.</p>
            <Button onClick={() => setShowCreate(true)}>Create Meeting</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rooms.map(room => (
              <div key={room.room_id} onClick={() => navigate(`/meet/${room.room_id}`)}
                className="rounded-xl border border-border bg-card hover:shadow-md transition-shadow cursor-pointer overflow-hidden group" data-testid={`room-${room.room_id}`}>
                <div className={`h-28 flex items-center justify-center ${room.is_active ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10' : 'bg-gradient-to-br from-gray-500/5 to-gray-500/10'}`}>
                  <Video className={`w-12 h-12 ${room.is_active ? 'text-green-500' : 'text-muted-foreground/30'}`} />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{room.name}</h3>
                    {room.is_active && <span className="text-[10px] bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded font-medium">Live</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">Host: {room.host_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(room.created_at).toLocaleDateString()}</span>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><Users className="w-3 h-3" /> {room.participants?.length || 0}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Meeting</DialogTitle></DialogHeader>
          <Input value={roomName} onChange={e => setRoomName(e.target.value)} placeholder="Meeting name" onKeyDown={e => e.key === 'Enter' && handleCreate()} data-testid="room-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="create-room-btn">Create & Join</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
